import React, { useState } from 'react';
import Table from '@components/table';
import styles from './styles.module.scss';
import Status from '@components/common/status';
import Typography from '@components/common/typography';
import Drawer from '@components/drawer';
import Input from '@components/common/input';
import Select from '@components/common/select';
import { useConsumers } from '@hooks/api/useConsumers';
import moment from 'moment/moment';
import { useNavigate, useParams } from 'react-router-dom';
import { getTagVariant, prettifyCardStatus, prettifyCardType } from '@utils/index';
import { ECardStatus, ECardType, TCard, TCardLite } from '@xeppt/xeppt-sdk/types';
import { cardStatuses, cardStatusesUpdates } from '@const/index';
import Dropdown from '@components/common/dropdown';
import { apiAdminService } from '@api';
import useModalContext from '@hooks/context/useModalContext';
import { modalIds } from '@const/modals';

const columns = (
    onView: (value: string) => void,
    onDetails: (value: string) => void,
    onAgreement: (value: string) => void,
    handleChangeCardStatus: (id: string, value: ECardStatus) => void
) => [
    { label: 'Card ID', key: 'id' },
    { label: 'Processor card ID', key: 'provider_id' },
    { label: 'Card number', key: 'card_number' },
    { label: 'Card limit', key: 'usage_limit' },
    {
        label: 'Expiration Date',
        key: 'expiration',
        render: (val: Date) => moment(val).format('MM.DD.YYYY HH:mm')
    },
    { label: 'Balance', key: 'balance', render: (val: number) => val?.toFixed(2) || '0.00' },
    { label: 'Currency', key: 'currency' },
    {
        label: 'Type',
        key: 'type',
        render: (value: ECardType) => prettifyCardType(value)
    },
    {
        label: 'Status',
        key: 'status',
        render: (value: ECardStatus, data: TCardLite) => (
            <Status
                variant={getTagVariant(value)}
                isUpdate={value === ECardStatus.ACTIVE}
                updateOptions={
                    value === ECardStatus.ACTIVE
                        ? cardStatusesUpdates.filter((status) => status.value !== value)
                        : []
                }
                //@ts-ignore
                onUpdate={(status) => handleChangeCardStatus(data.id, status as ECardStatus)}>
                {prettifyCardStatus(value)}
            </Status>
        )
    },
    {
        label: 'Date created',
        key: 'created_at',
        render: (val: Date) => moment(val).format('MM.DD.YYYY HH:mm')
    },
    {
        label: 'Actions',
        key: 'id',
        render: (value: string) => (
            <Dropdown
                items={[
                    {
                        label: 'View Transactions',
                        onClick: () => onView(value)
                    },
                    {
                        label: 'View Agreement',
                        onClick: () => onAgreement(value)
                    },
                    {
                        label: 'Details',
                        onClick: () => onDetails(value)
                    }
                ]}
            />
        )
    }
];

const CardsSection = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const navigate = useNavigate();
    const { id: consumer_id } = useParams<{ id: string }>();
    const { consumerCard, consumerCards, getConsumerCardById, handleRemoveCard } = useConsumers({
        isInitialConsumerCards: true
    });
    const { onOpen, changeModalData } = useModalContext();

    const handleChangeCardStatus = (card_id: string, status: ECardStatus) => {
        apiAdminService.updateConsumerCardStatus({
            account_id: consumer_id || '',
            card_id,
            status
        });
    };

    const handleViewCardDetails = (id: string) => {
        setIsDrawerOpen(true);
        getConsumerCardById(id);
    };

    const onViewAgreement = (id: string) => {
        console.log({ id });
        apiAdminService.getConsumerCardById(id).then((data) => {
            changeModalData(data);
            onOpen({
                modalId: modalIds.VIEW_AGREEMENT
            });
        });
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.text_wrapper}>
                    <Typography variant="h4">Cards</Typography>
                </div>
            </div>
            <Table
                columns={columns(
                    (cardId) => {
                        navigate(`?cardId=${cardId}`);
                    },
                    handleViewCardDetails,
                    onViewAgreement,
                    handleChangeCardStatus
                )}
                rows={consumerCards}
                emptyDescription="Cards list is empty"
            />
            <Drawer
                className={styles.drawer}
                isOpen={isDrawerOpen && !!consumerCard}
                width={670}
                onClose={() => {
                    setIsDrawerOpen(false);
                    handleRemoveCard();
                }}>
                <Typography className={styles.title} variant="h5">
                    Card details
                </Typography>
                <div className={styles.content}>
                    <Input
                        readOnly
                        value={consumerCard?.provider_id.toString()}
                        label="Processor ID"
                        full
                    />
                    <Input readOnly value={consumerCard?.card_number} label="Card number" full />
                    <Select
                        readOnly
                        value={consumerCard?.status}
                        label="Status"
                        full
                        onChange={() => {}}
                        items={cardStatuses}
                    />
                    <Input
                        readOnly
                        value={prettifyCardType(consumerCard?.type)}
                        label="Card Type"
                        full
                    />
                    <Input readOnly value={consumerCard?.currency} label="Currency" full />
                    <Input
                        readOnly
                        value={moment(consumerCard?.created_at).format('MM.DD.YYYY HH:mm')}
                        label="Created Date"
                        full
                    />
                    <Input
                        readOnly
                        value={moment(consumerCard?.expiration).format('MM.DD.YYYY HH:mm')}
                        label="Expiry Date"
                        full
                    />
                    <Input
                        readOnly
                        value={consumerCard?.usage_limit.toString()}
                        label="Usage Limit"
                        full
                    />
                    {/*<Input label="Available Limit" full />*/}
                    <Input readOnly value={consumerCard?.balance.toString()} label="Balance" full />
                    {/*<Input label="Reference" full />*/}
                </div>
            </Drawer>
        </div>
    );
};

export default CardsSection;
